import { Transportation } from 'model/NewMultipleBooking';
import { Moment } from 'moment-timezone';

export type TransportationLegType = 'pickup' | 'dropoff';

export type TransportationDateSlot = {
    date: Moment;
    dateEnd: Moment;
    type: TransportationLegType;
};

export const mergeTransportationDayWithClock = (day: Moment, clock: Moment, tz: string): Moment => {
    const d = day.clone().tz(tz).startOf('day');
    const c = clock.clone().tz(tz);
    return d.clone().hour(c.hour()).minute(c.minute()).second(0).millisecond(0);
};

const uniqSelectedDays = (selected: Array<Moment>, tz: string): Array<Moment> => {
    const uniqByDay = new Map<string, Moment>();
    selected.forEach(d => {
        const dayStart = d.clone().tz(tz).startOf('day');
        uniqByDay.set(dayStart.format('YYYY-MM-DD'), dayStart);
    });
    return Array.from(uniqByDay.values()).sort((a, b) => a.valueOf() - b.valueOf());
};

const getLegsToCreate = (transportation: Transportation): Array<TransportationLegType> => {
    if (transportation.transportType === 'roundtrip') {
        return ['dropoff', 'pickup'];
    }
    if (transportation.transportType === 'dropoff') {
        return ['dropoff'];
    }
    return ['pickup'];
};

const getLegClocks = (
    transportation: Transportation,
    leg: TransportationLegType,
    fallbackDate: Moment,
    tz: string
): { fromClock: Moment; toClock: Moment } => {
    const legData = leg === 'pickup' ? transportation.pickup : transportation.dropOff;
    const fromClock = (legData?.time?.from || fallbackDate).clone().tz(tz);
    const toClock =
        legData?.time?.to?.clone().tz(tz) || fromClock.clone().add(transportation.service?.duration || 60, 'minutes');
    return { fromClock, toClock };
};

export const normalizeTransportationForSlotSave = (transportation: Transportation, tz: string): Transportation => ({
    ...transportation,
    pickup: transportation.pickup
        ? {
              ...transportation.pickup,
              time: {
                  from: transportation.pickup.time.from?.clone().tz(tz),
                  to: transportation.pickup.time.to?.clone().tz(tz)
              }
          }
        : undefined,
    dropOff: transportation.dropOff
        ? {
              ...transportation.dropOff,
              time: {
                  from: transportation.dropOff.time.from?.clone().tz(tz),
                  to: transportation.dropOff.time.to?.clone().tz(tz)
              }
          }
        : undefined,
    selectedDates: transportation.selectedDates?.map(d => d.clone().tz(tz).startOf('day'))
});

export const getTransportationDateSlotsForSave = (
    transportation: Transportation,
    fallbackDate: Moment,
    tz: string,
    isEditMode: boolean,
    editLegType?: TransportationLegType
): Array<TransportationDateSlot> => {
    const normalized = normalizeTransportationForSlotSave(transportation, tz);
    const fallbackInTz = fallbackDate.clone().tz(tz);
    const legs = isEditMode
        ? [editLegType ?? (normalized.transportType === 'dropoff' ? 'dropoff' : 'pickup')]
        : getLegsToCreate(normalized);

    if (isEditMode) {
        return legs.map(leg => {
            const { fromClock, toClock } = getLegClocks(normalized, leg, fallbackInTz, tz);
            return { date: fromClock.clone(), dateEnd: toClock.clone(), type: leg };
        });
    }

    const selected = normalized.selectedDates ?? [];
    const days =
        selected.length > 0
            ? uniqSelectedDays(selected, tz)
            : [
                  (normalized.pickup?.time?.from || normalized.dropOff?.time?.from || fallbackInTz)
                      .clone()
                      .tz(tz)
                      .startOf('day')
              ];

    const slots: Array<TransportationDateSlot> = [];
    days.forEach(dayStart => {
        legs.forEach(leg => {
            const { fromClock, toClock } = getLegClocks(normalized, leg, fallbackInTz, tz);
            slots.push({
                date: mergeTransportationDayWithClock(dayStart, fromClock, tz),
                dateEnd: mergeTransportationDayWithClock(dayStart, toClock, tz),
                type: leg
            });
        });
    });

    return slots;
};

export const hasMultipleTransportationDates = (
    transportation: Transportation,
    fallbackDate: Moment,
    tz: string
): boolean => {
    const normalized = normalizeTransportationForSlotSave(transportation, tz);
    const selected = normalized.selectedDates ?? [];
    if (selected.length > 1) return true;
    if (selected.length === 1) return false;

    const days = new Set<string>();
    const pickupDay = normalized.pickup?.time?.from?.clone().tz(tz).startOf('day').format('YYYY-MM-DD');
    const dropOffDay = normalized.dropOff?.time?.from?.clone().tz(tz).startOf('day').format('YYYY-MM-DD');
    if (pickupDay) days.add(pickupDay);
    if (dropOffDay) days.add(dropOffDay);
    if (days.size === 0) {
        days.add(fallbackDate.clone().tz(tz).startOf('day').format('YYYY-MM-DD'));
    }
    return days.size > 1;
};
